/**
 * F35 (Wave 2F) — admin "override trainer billing" modal.
 *
 * Composes Modal + FormField + Select (plan) + NumberInput (rate %, persisted
 * as basis points — only meaningful for the commission plan) + a native date
 * input (trial ends at) + Textarea (reason ≥10) + forward-only Banner.
 *
 * Per D10 the override applies IMMEDIATELY (not queued); per D11 it is
 * forward-only — issued invoices are never rewritten. The server gates the
 * write on `trainer_billing:manage`; the page hides the action without it.
 *
 * Validation mirrors `AdminTrainerBillingOverrideInputSchema`:
 *   - reason 10–500 chars (mandatory)
 *   - rateBasisPoints integer in [0, 5000] (0%–50%)
 *   - at least one of { mode, rate, trial } must change
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, NumberInput, Select, Textarea } from '@bravemove/ui-web';
import {
  AdminTrainerBillingOverrideInputSchema,
  TRAINER_BILLING_MODES,
  TRAINER_MAX_RATE_BASIS_POINTS,
  TRAINER_MIN_RATE_BASIS_POINTS,
  type AdminTrainerBillingOverrideInput,
  type TrainerBillingMode,
} from '@bravemove/types';

export interface TrainerBillingOverrideDialogProps {
  readonly open: boolean;
  readonly trainerUserId: string;
  readonly trainerLabel: string;
  readonly currentMode: TrainerBillingMode;
  readonly currentRateBasisPoints: number;
  readonly currentTrialEndsAt: string | null;
  readonly onConfirm: (input: AdminTrainerBillingOverrideInput) => Promise<void>;
  readonly onClose: () => void;
}

const MIN_PERCENT = TRAINER_MIN_RATE_BASIS_POINTS / 100; // 0
const MAX_PERCENT = TRAINER_MAX_RATE_BASIS_POINTS / 100; // 50

function bpToPercent(bp: number): number {
  return Math.round((bp / 100) * 100) / 100;
}

function percentToBp(pct: number): number {
  return Math.round(pct * 100);
}

/** Convert an ISO datetime to a yyyy-mm-dd value for a native date input. */
function isoToDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Convert a yyyy-mm-dd date-input value to an ISO datetime (midnight UTC). */
function dateInputToIso(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function TrainerBillingOverrideDialog({
  open,
  trainerUserId,
  trainerLabel,
  currentMode,
  currentRateBasisPoints,
  currentTrialEndsAt,
  onConfirm,
  onClose,
}: TrainerBillingOverrideDialogProps) {
  const { t } = useI18n();

  const [mode, setMode] = useState<TrainerBillingMode>(currentMode);
  const [ratePercent, setRatePercent] = useState<number>(bpToPercent(currentRateBasisPoints));
  const [trialDate, setTrialDate] = useState<string>(isoToDateInput(currentTrialEndsAt));
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset the dialog's controlled form state each time it (re-)opens so a
    // second open after a cancel starts from the trainer's current values.
    // The setStates here are an intentional open→form-state sync, not a render
    // cascade (mirrors the F23 EditRateDialog reset-on-open pattern).
    /* eslint-disable react-hooks/set-state-in-effect */
    setMode(currentMode);
    setRatePercent(bpToPercent(currentRateBasisPoints));
    setTrialDate(isoToDateInput(currentTrialEndsAt));
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, currentMode, currentRateBasisPoints, currentTrialEndsAt]);

  const rateBasisPoints = percentToBp(Number(ratePercent ?? 0));

  // Which fields changed (drives the "at least one" guard + the payload shape).
  const modeChanged = mode !== currentMode;
  const rateChanged = rateBasisPoints !== currentRateBasisPoints;
  const trialChanged = isoToDateInput(currentTrialEndsAt) !== trialDate;
  const somethingChanged = modeChanged || rateChanged || trialChanged;

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const rateValid =
    rateBasisPoints >= TRAINER_MIN_RATE_BASIS_POINTS &&
    rateBasisPoints <= TRAINER_MAX_RATE_BASIS_POINTS;

  const modeOptions = useMemo(
    () =>
      TRAINER_BILLING_MODES.map((m) => ({
        value: m,
        label: t(`trainer-billing:mode.${m}`, {
          defaultValue: m === 'commission' ? 'Commission' : 'Flat monthly',
        }),
      })),
    [t],
  );

  const disableSubmit = submitting || !somethingChanged || !reasonValid || !rateValid;

  const buildPayload = (): AdminTrainerBillingOverrideInput => {
    const payload: Record<string, unknown> = { reason: reasonTrimmed };
    if (modeChanged) payload.mode = mode;
    if (rateChanged) payload.rateBasisPoints = rateBasisPoints;
    if (trialChanged) payload.trialEndsAt = trialDate ? dateInputToIso(trialDate) : null;
    return payload as AdminTrainerBillingOverrideInput;
  };

  const submit = async () => {
    setServerError(null);
    if (!somethingChanged) {
      setServerError(
        t('trainer-billing:errors.TRAINER_BILLING_OVERRIDE_EMPTY', {
          defaultValue: 'Provide at least one of plan, rate, or trial to override.',
        }),
      );
      return;
    }
    const candidate = buildPayload();
    const parsed = AdminTrainerBillingOverrideInputSchema.safeParse(candidate);
    if (!parsed.success) {
      setServerError(
        t('trainer-billing:errors.TRAINER_RATE_OVERRIDE_OUT_OF_BOUNDS', {
          defaultValue: 'The rate must be between 0% and 50%.',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(parsed.data);
      onClose();
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code: string }).code
          : null;
      const key = code && code.startsWith('TRAINER_') ? `trainer-billing:errors.${code}` : null;
      setServerError(
        key
          ? t(key, { defaultValue: 'Could not apply the override. Please try again.' })
          : t('admin:commission.toast.error_generic', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('trainer-billing:admin.override_title', {
        defaultValue: 'Override billing for this trainer',
      })}
      description={t('trainer-billing:admin.override_reason_hint', {
        defaultValue:
          'Audit-logged. Applies immediately, forward-only — issued invoices are never rewritten.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="trainer-billing-override-cancel"
          >
            {/* CST-010 — dedicated key (queued.cancel renders "Cancel switch"). */}
            {t('trainer-billing:admin.dialog_cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="trainer-billing-override-confirm"
          >
            {t('trainer-billing:admin.override_submit', { defaultValue: 'Apply override' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="trainer-billing-override-dialog"
        data-trainer-id={trainerUserId}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-[12px] text-ink-3">
          {/* CST-010 — dedicated key (invoices.detail.title renders "Invoice"). */}
          {t('trainer-billing:admin.trainer_label', { defaultValue: 'Trainer' })}:{' '}
          <span className="font-semibold text-ink">{trainerLabel}</span>
        </p>

        <div data-testid="trainer-billing-override-forward-only-banner">
          {/* CST-010 — dedicated key (override_reason renders "Reason (required)"). */}
          <Banner
            tone="info"
            title={t('trainer-billing:admin.forward_only_title', { defaultValue: 'Forward-only' })}
          >
            {t('trainer-billing:admin.override_reason_hint', {
              defaultValue:
                'Audit-logged. Applies immediately, forward-only — issued invoices are never rewritten.',
            })}
          </Banner>
        </div>

        <FormField label={t('trainer-billing:admin.override_mode', { defaultValue: 'Plan' })}>
          <Select
            value={mode}
            options={modeOptions}
            data-testid="trainer-billing-override-mode"
            aria-label={t('trainer-billing:admin.override_mode', { defaultValue: 'Plan' })}
            onChange={(e) => setMode(e.target.value as TrainerBillingMode)}
          />
        </FormField>

        {mode === 'commission' ? (
          <FormField
            label={t('trainer-billing:admin.override_rate', {
              defaultValue: 'Commission rate (%)',
            })}
            helper={t('trainer-billing:current.rate_value', {
              percent: '0–50',
              defaultValue: 'Between 0% and 50%.',
            })}
          >
            <NumberInput
              value={ratePercent}
              min={MIN_PERCENT}
              max={MAX_PERCENT}
              step={0.01}
              data-testid="trainer-billing-override-rate-input"
              aria-label={t('trainer-billing:admin.override_rate', {
                defaultValue: 'Commission rate (%)',
              })}
              onChange={(v) => setRatePercent(Number(v))}
            />
          </FormField>
        ) : null}

        <FormField
          label={t('trainer-billing:admin.override_trial', { defaultValue: 'Trial ends at' })}
        >
          <input
            type="date"
            value={trialDate}
            data-testid="trainer-billing-override-trial-input"
            aria-label={t('trainer-billing:admin.override_trial', {
              defaultValue: 'Trial ends at',
            })}
            onChange={(e) => setTrialDate(e.target.value)}
            className="h-10 w-full rounded-[10px] border border-hairline-2 bg-surface px-3.5 text-[13.5px] text-ink focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent/25"
          />
        </FormField>

        <FormField
          label={t('trainer-billing:admin.override_reason', {
            defaultValue: 'Reason (required)',
          })}
          helper={t('trainer-billing:admin.override_reason_hint', {
            defaultValue:
              'Audit-logged. Applies immediately, forward-only — issued invoices are never rewritten.',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="trainer-billing-override-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="trainer-billing-override-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default TrainerBillingOverrideDialog;
